import { useState } from "react"
import { Button, CircularProgress, Stack, Typography, Box, useMediaQuery, type Theme } from "@mui/material"
import { useDispatch } from "react-redux"
import { trocarView, type MudancaKiosk } from "../navegacao-kiosk"
import BalancaResumo from "./balanca-resumo"
import AddIcon from '@mui/icons-material/Add'
import PeopleIcon from '@mui/icons-material/People'
import PaymentIcon from '@mui/icons-material/Payment'

type AcaoFooter = 'adicionar' | 'dividir' | 'pagar'

interface ItemsFooterProps {
    bloqueado?: boolean
}

function ItemsFooter({ bloqueado = false }: ItemsFooterProps): JSX.Element {
    const dispatch = useDispatch()
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))
    const [acao, setAcao] = useState<AcaoFooter | null>(null)
    const ocupado = acao !== null
    const desabilitado = (propria: AcaoFooter): boolean => bloqueado || (ocupado && acao !== propria)

    const navegar = (nova: AcaoFooter, mudanca: MudancaKiosk): void => {
        if (ocupado || bloqueado) return
        setAcao(nova)

        requestAnimationFrame(() => requestAnimationFrame(() => trocarView(dispatch, mudanca)))
    }

    const onClickAddItem = (): void => {
        navegar('adicionar', { currentView: 'VIEW_ADD_ITEMS' })
    }

    const onClickPayment = (): void => {
        navegar('pagar', { currentView: 'VIEW_PAYMENT', modoPagamento: 'CONTA_INTEIRA' })
    }

    const onClickDividirConta = (): void => {
        navegar('dividir', { currentView: 'VIEW_PAYMENT', modoPagamento: 'ESCOLHER_VALOR' })
    }

    return (
        <Box sx={{ width: '100%', mt: 'auto', position: 'relative', flexShrink: 0 }}>
            <Box sx={{ p: 2, pt: 0 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        sx={{ py: 2, borderRadius: 3, textTransform: 'none', flex: isMobile ? '1 1 calc(50% - 8px)' : '1 1 0' }}
                        onClick={onClickAddItem}
                        disabled={desabilitado('adicionar')}
                    >
                        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                            {acao === 'adicionar' ? (
                                <CircularProgress size={isMobile ? 35 : 24} color="inherit" />
                            ) : (
                                <AddIcon fontSize={!isMobile ? "medium" : "large"} />
                            )}

                            <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1 }}>
                                Adicionar Item
                            </Typography>
                        </Stack>
                    </Button>

                    <Button
                        color="primary"
                        variant="outlined"
                        onClick={onClickDividirConta}
                        disabled={desabilitado('dividir')}
                        sx={{ py: 2, borderRadius: 3, textTransform: 'none', flex: isMobile ? '1 1 calc(50% - 8px)' : '1 1 0' }}
                    >
                        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                            {acao === 'dividir' ? (
                                <CircularProgress size={isMobile ? 35 : 24} color="inherit" />
                            ) : (
                                <PeopleIcon fontSize={!isMobile ? "medium" : "large"} />
                            )}

                            <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1 }}>
                                Dividir Conta
                            </Typography>
                        </Stack>
                    </Button>

                    <Button
                        color="primary"
                        variant="contained"
                        onClick={onClickPayment}
                        disabled={desabilitado('pagar')}
                        sx={{ py: 2, borderRadius: 3, textTransform: 'none', flex: isMobile ? '1 1 100%' : '1 1 0' }}
                    >
                        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', justifyContent: 'center' }}>
                            {acao === 'pagar' ? (
                                <CircularProgress size={isMobile ? 35 : 24} color="inherit" />
                            ) : (
                                <PaymentIcon fontSize={!isMobile ? "medium" : "large"} />
                            )}

                            <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1 }}>
                                Pagar
                            </Typography>
                        </Stack>
                    </Button>
                </Box>

                <BalancaResumo ativo={!bloqueado} />
            </Box>
        </Box>
    )
}

export default ItemsFooter
